import React, { useRef, useState } from 'react';
import { Upload, Trash2, Loader2, Link2, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  whatsappCloudService, WhatsAppCloudTemplate, MEDIA_RULES, headerFormatOf,
} from '@/services/integrations/whatsappCloudService';

interface Props {
  template: WhatsAppCloudTemplate;
  /** Called with the new URL (or null when cleared) so the parent can refresh. */
  onChange: (url: string | null) => void;
}

/**
 * Attach the send-time media source for a media-header template.
 *
 * Meta keeps only an approval sample of the header asset, so a media-header
 * template can't actually be sent until something supplies real media per
 * message. Uploading here is a Spaces write plus a metadata update — no Graph
 * API call, and no re-approval, because the template is already approved.
 */
const HeaderMediaPanel: React.FC<Props> = ({ template, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [urlMode, setUrlMode] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const format = headerFormatOf(template);
  const rules = MEDIA_RULES[format];
  const attached = template.header_media_url;

  const save = async (source: File | string) => {
    setBusy(true);
    try {
      const res = await whatsappCloudService.attachHeaderMedia(template.id, source);
      onChange(res.header_media_url);
      setUrlMode(false);
      setUrlInput('');
      toast.success('Header media attached — this template can now be sent');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not attach media');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await whatsappCloudService.clearHeaderMedia(template.id);
      onChange(null);
      toast.success('Header media removed');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not remove media');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="text-xs font-medium text-gray-500 mb-2">Send-time {format.toLowerCase()}</div>

      {attached ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-green-800">Ready to send</p>
              <a
                href={attached}
                target="_blank"
                rel="noreferrer"
                className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-600 hover:text-brandColor break-all"
              >
                {attached} <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            </div>
            <button
              onClick={clear}
              disabled={busy}
              title="Remove — the template stops being sendable"
              className="text-gray-400 hover:text-red-500 flex-shrink-0 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
          </div>
          {format === 'IMAGE' && (
            <img
              src={attached}
              alt=""
              className="mt-2 rounded max-h-32 w-auto border border-green-200"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs text-amber-800 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Journeys can't send this template yet. Meta stored only an approval sample —
              every message needs its own {format.toLowerCase()}.
            </span>
          </p>

          <input
            ref={fileRef}
            type="file"
            accept={rules?.accept}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) save(f); }}
            className="hidden"
          />

          {urlMode ? (
            <div className="mt-3 flex items-center gap-2">
              <input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://…"
                className="flex-1 px-2 py-1.5 text-xs border border-amber-300 rounded-lg outline-none focus:border-brandColor bg-white"
              />
              <button
                onClick={() => save(urlInput.trim())}
                disabled={busy || !urlInput.trim()}
                className="px-3 py-1.5 text-xs rounded-lg bg-brandColor text-white disabled:opacity-50"
              >
                Save
              </button>
              <button onClick={() => setUrlMode(false)} className="text-xs text-gray-500 hover:text-gray-700">
                Cancel
              </button>
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-brandColor text-white hover:opacity-90 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {busy ? 'Uploading…' : `Upload ${format.toLowerCase()}`}
              </button>
              <button
                onClick={() => setUrlMode(true)}
                disabled={busy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
              >
                <Link2 className="w-3.5 h-3.5" /> Use a URL
              </button>
            </div>
          )}

          {rules && (
            <p className="mt-2 text-[11px] text-amber-700/70">
              {rules.accept.replace(/[a-z]+\//g, '').toUpperCase()} · up to {rules.maxMB}MB ·
              stored in DigitalOcean Spaces
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderMediaPanel;
